import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { FaCode, FaEdit } from 'react-icons/fa';
import { IoDuplicate } from 'react-icons/io5';
import { BsTriangleHalf } from 'react-icons/bs';
import { API } from '../../api/Api';
import { BeatLoader } from 'react-spinners';
import { toast, Toaster } from 'react-hot-toast';
import VisualizerDropDown from '../components/Dropdowns/VisualizerDropDown';
import { GrNext, GrPrevious } from 'react-icons/gr';

const AddLayouts = () => {
    const token = localStorage.getItem('token');
    const [apiData, setApiData] = useState([]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [modalImage, setModalImage] = useState(false);
    const [showLayout, setshowLayout] = useState(false);
    const [deleteLayoutId, setDeleteLayoutId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;


    const [formData, setFormData] = useState({
        id: '0',
        name: apiData?.name || '',
        visualizertype_id: '',
        info: '',
        layout_img: null,
        layout_image_path: '',
        layout_name: '',
        key: '', // Initialize key as an empty string
        contact_person_name: '',
        contact_person_email: '',
        status: '1',
    });

    const { id } = useParams();

    const fetchVisualizerDetails = async () => {
        const baseURl = process.env.REACT_APP_API_URL;
        const apiPath = '/visualizer/getvisualizerdetail';
        const url = `${baseURl}${apiPath}`;
        const requestBody = { visualizer_id: id };

        try {
            setLoading(true);
            const response = await API(url, requestBody, token);
            if (response) {
                setApiData(response.Result_value);
            }
        } catch (error) {
            console.error('Error during authentication:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVisualizerDetails();
    }, []);

    useEffect(() => {
        if (apiData) {
            setFormData(prevFormData => ({
                ...prevFormData,
                name: apiData.name, // Set name to the visualizer name from apiData
                parent_id: id, // Set parent_id to the main visualizer id
                key: apiData.key || '', // Set key if it exists in the API response
            }));
        }
    }, [apiData, id]);

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        setErrors((prev) => ({ ...prev, [name]: '' }));

        if (type === 'file') {
            const file = files[0];
            if (file) {
                const validTypes = ['image/png', 'image/webp'];
                if (!validTypes.includes(file.type)) {
                    setErrors((prev) => ({ ...prev, [name]: 'Only PNG and WebP files are allowed' }));
                    return;
                }
            }
            setFormData((prevData) => ({ ...prevData, [name]: files[0] }));
        } else {
            setFormData((prevData) => ({ ...prevData, [name]: value }));
        }
    };


    const handleEdit = (layout) => {
        setFormData({
            id: layout.id,
            name: layout.name,
            visualizertype_id: layout.visualizertype_id,
            info: layout.info,
            layout_img: null, // You might want to handle image previews separately
            layout_image_path: layout.layout_image, // You might want to handle image previews separately
            layout_name: layout.layout_name,
            key: layout.key || '', // Set key if it exists in the layout data
            contact_person_name: layout.contact_person_name,
            contact_person_email: layout.contact_person_email,
            status: layout.status ? '1' : '0', // Ensure status is set correctly
        });
        setshowLayout(true); // Open the modal
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!formData.visualizertype_id) newErrors.visualizertype_id = 'Visualizer type is required';
        if (!formData.info) newErrors.info = 'Info is required';
        if (!formData.layout_img && !formData.layout_image_path) newErrors.layout_img = 'layout_image is required';
        if (!formData.layout_name) newErrors.layout_name = 'Layout Name is required';
        if (!formData.contact_person_name) newErrors.contact_person_name = 'Contact person name is required';
        if (!formData.contact_person_email) newErrors.contact_person_email = 'Contact person email is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);

        const url = `${process.env.REACT_APP_API_URL}/visualizer/createupdatevisualizer`;

        const formDataToSend = new FormData();
        formDataToSend.append('id', formData.id);
        formDataToSend.append('name', formData.name);
        formDataToSend.append('visualizertype_id', formData.visualizertype_id);
        formDataToSend.append('info', formData.info);

        // Check if a new file is uploaded
        if (formData.layout_img instanceof File) {
            formDataToSend.append('layout_image', formData.layout_img);
            formDataToSend.append('layout_image_path', ''); // Set layout_image_path to empty if a new file is uploaded
        } else {
            formDataToSend.append('layout_image', ''); // Send empty layout_image parameter if no new file is uploaded
            formDataToSend.append('layout_image_path', formData.layout_image_path || ''); // Send existing layout_image_path
        }

        formDataToSend.append('layout_name', formData.layout_name);
        formDataToSend.append('key', formData.key);
        formDataToSend.append('parent_id', id);
        formDataToSend.append('contact_person_name', formData.contact_person_name);
        formDataToSend.append('contact_person_email', formData.contact_person_email);
        formDataToSend.append('status', formData.status);

        try {
            const response = await API(url, formDataToSend, token, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            if (response?.Result === 1) {
                toast.success(formData.id === '0' ? 'Layout added successfully!' : 'Layout updated successfully!');
                setshowLayout(false); // Close the modal after successful submission
                fetchVisualizerDetails(); // Refresh the data to include the new layout
            } else {
                toast.error('Failed to save layout.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Something went wrong!');
        } finally {
            setLoading(false);
        }
    };


    const navigate = useNavigate();

    const openModal = (imageSrc) => {
        setModalImage(imageSrc);
    };

    const handleTypeSelect = (visualizertype_id) => {
        setFormData((prevData) => ({
            ...prevData,
            visualizertype_id: visualizertype_id || "",
        }));
    };


    const handleDeleteLayout = async (id) => {
        const deleteUrl = `${process.env.REACT_APP_API_URL}/visualizer/deletevisualizer`;
        try {
            setLoading(true);
            const res = await API(deleteUrl, { id }, token);

            if (res?.Result === 1) {
                toast.success('Layout deleted successfully');
                fetchVisualizerDetails(); // Refresh the data to exclude the deleted layout
            } else {
                toast.error('Failed to delete layout');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Something went wrong');
        } finally {
            setDeleteLayoutId(null); // Close the confirmation modal
            setLoading(false);
        }
    };

    const totalPages = Math.ceil((apiData?.Layouts?.length || 0) / itemsPerPage);
    const indexOfLast = currentPage * itemsPerPage;
    const indexOfFirst = indexOfLast - itemsPerPage;
    const currentLayouts = apiData?.Layouts?.slice(indexOfFirst, indexOfLast) || [];



    return (
        <>
            {loading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/80">
                    <BeatLoader size={20} />
                </div>
            )}

            <Toaster position="top-center" />

            <div className="flex h-screen fixed w-full">
                <div className="md:w-64 w-16 bg-black transition-all">
                    <Sidebar />
                </div>
                <div className="flex flex-col flex-1 min-h-full">
                    <div className="bg-gray-100 flex-1 p-4 overflow-auto min-h-full">
                        <div className="bg-white rounded-2xl w-full flex flex-col min-h-full">
                            {/* Header */}
                            <div className="flex justify-between items-center px-6 py-4">
                                <p className="text-teal-500 text-xl font-semibold">Layouts</p>
                                <button
                                    onClick={() => navigate('/admin/layouts')}
                                    className="bg-gradient-to-r from-teal-400 to-teal-500 text-white rounded px-4 py-2 hover:from-teal-500 hover:to-teal-600 transition shadow-md"
                                >
                                    Back
                                </button>
                            </div>
                            <hr className="border-gray-200" />

                            {/* Add Angle Button */}
                            <div className="flex justify-between items-center px-6 mt-4">
                                <div>
                                    <p className='text-lg text-red-black font-semibold'>Layouts for {apiData?.name}</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setFormData(prevFormData => ({
                                            ...prevFormData,
                                            id: '0',
                                            name: apiData?.name || '',
                                            visualizertype_id: '',
                                            info: '',
                                            layout_img: null,
                                            layout_name: '',
                                            contact_person_name: '',
                                            contact_person_email: '',
                                            status: '1',
                                        }));
                                        setshowLayout(true);
                                    }}
                                    className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                                >
                                    Add Layout
                                </button>
                            </div>

                            {/* Table */}
                            <div className="p-6">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full border-collapse bg-white rounded-lg shadow-md">
                                        <thead>
                                            <tr className="bg-gray-100 text-gray-800">
                                                <th className="text-start p-3 font-medium">ID</th>
                                                {/* <th className="text-start p-3 font-medium">Layout ID</th> */}
                                                <th className="text-start p-3 font-medium">Layout Name</th>
                                                <th className="text-start p-3 font-medium">Layout Image</th>
                                                <th className="text-start p-3 font-medium">Contact Person</th>
                                                <th className="text-start p-3 font-medium">Angles</th>
                                                <th className="text-start p-3 font-medium">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentLayouts?.length > 0 ? (
                                                currentLayouts.map((layout, index) => (
                                                    <tr key={layout.id} className="border-b hover:bg-gray-50">
                                                        <td className="p-3">{indexOfFirst + index + 1}</td>
                                                        {/* <td className="p-3">{layout.id}</td> */}
                                                        <td className="p-3 text-blue-600 hover:underline cursor-pointer">
                                                            <a href={`/admin/colordetail/${layout.id}`}>{layout.layout_name}</a>
                                                        </td>
                                                        <td className="p-3">
                                                            {layout.layout_image ? (
                                                                <img
                                                                    src={layout.layout_image}
                                                                    alt="Layout"
                                                                    className="w-16 h-16 object-cover rounded cursor-pointer hover:scale-105 transition"
                                                                    onClick={() => openModal(layout.layout_image)}
                                                                />
                                                            ) : (
                                                                <span className="text-gray-400 text-sm italic">No Image</span>
                                                            )}
                                                        </td>

                                                        <td className="p-3">{layout.contact_person_name}</td>
                                                        <td className="p-3">
                                                            <BsTriangleHalf size={20} title='Diff. Angles' onClick={() => navigate(`/admin/angle/${layout.id}`)} className='cursor-pointer' />
                                                        </td>
                                                        <td className="p-3 align-middle">
                                                            <div className="flex items-center  gap-2 h-full">
                                                                <button onClick={() => handleEdit(layout)} className="rounded py-1" title='Edit Layout'>
                                                                    <FaEdit size={20} />
                                                                </button>
                                                                <div title='Delete' className='cursor-pointer'>
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        strokeWidth="2"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        className="w-5 h-5 text-red-500 hover:text-red-600 transition"
                                                                        onClick={() => setDeleteLayoutId(layout.id)}
                                                                    >
                                                                        <path d="M3 6h18M19 6l-1 14H6L5 6M9 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M12 10v6M15 10v6M9 10v6" />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        </td>

                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center p-4 text-gray-500">No Layouts Found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    {apiData?.Layouts?.length > itemsPerPage && (
                                        <div className="flex justify-center items-center gap-4 mt-4">
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:opacity-50"
                                            >
                                                <GrPrevious />
                                            </button>

                                            <span className="text-gray-700">
                                                Page {currentPage} of {totalPages}
                                            </span>

                                            <button
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                disabled={currentPage === totalPages}
                                                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:opacity-50"
                                            >
                                                <GrNext />
                                            </button>
                                        </div>
                                    )}

                                </div>
                            </div>

                            {/* Add Layout Modal */}
                            {showLayout && (
                                <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex justify-center items-center overflow-auto">
                                    <div className="bg-white p-6 rounded-xl w-full max-w-[50%] h-[90%] overflow-auto shadow-xl">
                                        <div className='flex justify-between'>
                                            <h2 className="text-lg font-semibold text-gray-800 mb-4">{formData.id === '0' ? 'Add New Layout' : 'Edit Layout'}</h2>
                                            <p className='text-red-500 cursor-pointer' onClick={() => setshowLayout(false)}>X</p>
                                        </div>
                                        <form onSubmit={handleSubmit} className="my-4 gap-6 m-auto text-sm">
                                            <div className="grid grid-cols-1 md:grid-cols-2">
                                                <div className='m-4'>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        className="w-full border-b py-2 px-1 outline-none bg-gray-200"
                                                        disabled // Disable the input field
                                                    />
                                                    {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
                                                </div>

                                                <div className='m-4'>
                                                    <VisualizerDropDown
                                                        value={formData.visualizertype_id}
                                                        onTypeSelect={handleTypeSelect}
                                                        name="visualizertype_id"
                                                        className={`block py-3 mt- px-0 w-full text-sm text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none `}
                                                    />
                                                    <label
                                                        htmlFor="visualizertype_id"
                                                        className={`absolute text-xs md:text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-1 -z-10 origin-[0] transition-all ease-in-out
                                            ${formData.visualizertype_id ? 'peer-focus:text-blue-600' : 'peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0'}
                                            peer-focus:-translate-y-6 peer-focus:scale-75`}
                                                    >
                                                        Visualizer Type <span className="text-red-500">*</span>
                                                    </label>
                                                    {errors.visualizertype_id && <p className="text-red-500 text-xs">{errors.visualizertype_id}</p>}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-1">
                                                <div className='m-4'>
                                                    <textarea
                                                        name="info"
                                                        placeholder="Info *"
                                                        value={formData.info}
                                                        onChange={handleChange}
                                                        className="w-full border-b py-2 px-1 outline-none"
                                                    />
                                                    {errors.info && <p className="text-red-500 text-xs">{errors.info}</p>}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2">
                                                <div className='m-4'>
                                                    <label htmlFor="" className='text-gray-400'>Layout Name<span className='text-red-500'>*</span></label>
                                                    <input
                                                        name="layout_name"
                                                        value={formData.layout_name}
                                                        onChange={handleChange}
                                                        className="w-full border-b py-2 px-1 outline-none"
                                                    />
                                                    {errors.layout_name && <p className="text-red-500 text-xs">{errors.layout_name}</p>}
                                                </div>

                                                <div className='m-4'>
                                                    <label htmlFor="layout_image" className='text-gray-400'>Layout Image <span className='text-red-500'>*</span></label>
                                                    {formData.layout_img ? (
                                                        <div className="mb-4">
                                                            <img
                                                                src={URL.createObjectURL(formData.layout_img)}
                                                                alt="Preview"
                                                                className="w-28 object-contain mb-4"
                                                            />
                                                        </div>
                                                    ) : formData.layout_image_path ? (
                                                        <div className="mb-4">
                                                            <img
                                                                src={formData.layout_image_path}
                                                                alt="Existing Layout"
                                                                className="w-28 object-contain mb-4"
                                                            />
                                                        </div>
                                                    ) : null}
                                                    <input
                                                        type="file"
                                                        name="layout_img"
                                                        accept=".png,.webp"
                                                        onChange={handleChange}
                                                        className="w-full py-2 px-1 border rounded text-gray-400"
                                                    />
                                                    {errors.layout_img && <p className="text-red-500 text-xs">{errors.layout_img}</p>}
                                                </div>

                                            </div>


                                            <div className="grid grid-cols-1 md:grid-cols-2">
                                                <div className='m-4'>
                                                    <input
                                                        type="text"
                                                        name="contact_person_name"
                                                        placeholder="Contact Person Name *"
                                                        value={formData.contact_person_name}
                                                        onChange={handleChange}
                                                        className="w-full border-b py-2 px-1 outline-none"
                                                    />
                                                    {errors.contact_person_name && <p className="text-red-500 text-xs">{errors.contact_person_name}</p>}
                                                </div>
                                                <div className='m-4'>
                                                    <input
                                                        type="email"
                                                        name="contact_person_email"
                                                        placeholder="Contact Person Email *"
                                                        value={formData.contact_person_email}
                                                        onChange={handleChange}
                                                        className="w-full border-b py-2 px-1 outline-none"
                                                    />
                                                    {errors.contact_person_email && <p className="text-red-500 text-xs">{errors.contact_person_email}</p>}
                                                </div>
                                            </div>

                                            <div className="flex items-center space-x-2 m-4">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.status === '1'}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        status: e.target.checked ? '1' : '0',
                                                    }))}
                                                />
                                                <label>Active</label>
                                            </div>

                                            <div className="col-span-2 flex justify-end mt-4">
                                                <button
                                                    type="submit"
                                                    disabled={loading}
                                                    className="bg-gradient-to-r from-teal-400 to-teal-500 text-white px-6 py-2 rounded shadow hover:from-teal-500 hover:to-teal-600"
                                                >
                                                    {loading ? 'Submitting...' : 'Submit'}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {modalImage && (
                                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                                    <div className="relative bg-white p-6 rounded-xl shadow-lg">
                                        <img src={modalImage} alt="Full View" className="max-w-full max-h-[80vh] rounded-lg" />
                                        <button
                                            onClick={() => setModalImage(false)}
                                            className="absolute top-3 right-3 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600"
                                        >
                                            X
                                        </button>
                                    </div>
                                </div>
                            )}

                            {deleteLayoutId && (
                                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                                    <div className="bg-white p-8 rounded-xl shadow-lg text-gray-800">
                                        <p className="text-lg mb-6">Are you sure you want to delete this layout and All the Associations with this?</p>
                                        <div className="flex gap-4 justify-center">
                                            <button
                                                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
                                                onClick={() => handleDeleteLayout(deleteLayoutId)}
                                            >
                                                Yes
                                            </button>
                                            <button
                                                className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400"
                                                onClick={() => setDeleteLayoutId(null)}
                                            >
                                                No
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddLayouts;
